import { classes as htmlClasses } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { forwardRef } from 'react';
import { Link } from 'react-router-dom';

import { company } from '~/company/Company';
import {
  cfSrcForImage,
  createCategoryUrl,
} from '~/components/CategoryBlocks/utils';
import { useCategoryAnalytics } from '~/hooks/useCategoryAnalytics';
import { useGlobal } from '~/hooks/useGlobal';

import { CategoryBlockBackgroundImage } from '../CategoryBlockBackgroundImage';
import {
  bentoTileRatios,
  bentoTileSizes,
  resolveTileSpans,
  TILE_DESKTOP_MIN_WIDTH,
  TILE_WIDTHS,
} from '../GridView';
import styles from './Card.module.scss';
import type { CardProps } from './interfaces';

/**
 * Sizes for the DubaiExpress clean tile, whose image is a contained product
 * cutout rather than a full-bleed background. Kept exactly as they were before
 * the background moved to a responsive image: that look is not what this ticket
 * is about, and a phone has no use for the desktop pixels.
 */
const CLEAN_TILE_SIZE_MOBILE = { width: 360, height: 360 };
const CLEAN_TILE_SIZE_DESKTOP = { width: 560, height: 560 };

const Card = forwardRef<HTMLAnchorElement, CardProps>(
  (
    {
      classes,
      category,
      tileSize = 1,
      imageSizes,
      imageAspectRatio,
      priority = false,
    },
    ref,
  ) => {
    const { name, id, previewImage } = category;
    const { root: className, titleClassName } = classes || {};

    const { handleClickCategory } = useCategoryAnalytics();
    const { isMobile } = useGlobal();

    // DubaiExpress uses the clean home-page tile look (light card, dark
    // centered title, contained image) instead of a full-bleed photo.
    const isCleanTile = company.isDubaiExpressCompany;

    const spans = resolveTileSpans(tileSize);
    const tileRatio = imageAspectRatio ?? bentoTileRatios(spans);
    const tileSizes = imageSizes ?? bentoTileSizes(spans);

    return (
      <Link
        to={createCategoryUrl({ id })}
        onClick={handleClickCategory(id)}
        ref={ref}
        className={htmlClasses([
          styles.root,
          isCleanTile && styles.cleanTile,
          className,
        ])}
      >
        {!isCleanTile && previewImage && (
          <CategoryBlockBackgroundImage
            priority={priority}
            mobile={{
              src: previewImage,
              aspectRatio: tileRatio.mobile,
              sizes: tileSizes,
              widths: TILE_WIDTHS,
            }}
            desktop={{
              src: previewImage,
              aspectRatio: tileRatio.desktop,
              sizes: tileSizes,
              widths: TILE_WIDTHS,
            }}
            desktopMinWidth={TILE_DESKTOP_MIN_WIDTH}
            className="absolute inset-0"
          />
        )}
        <h4
          // `hyphens: auto` needs a language with a hyphenation dictionary, but
          // the document is hardcoded to `lang="en"`, which never hyphenates
          // non-Latin category names. Spanish rules are purely vowel-based and
          // hyphenate any script, so they stand in as the generic dictionary.
          // The clean tile keeps `hyphens: manual`, so it needs no override.
          lang={isCleanTile ? undefined : 'es'}
          className={htmlClasses([
            styles.title,
            // Block-level title overrides style the photo-tile look (white,
            // photo-anchored) and would break the clean tile — skip them there.
            !isCleanTile && titleClassName,
          ])}
        >
          {name}
        </h4>
        {isCleanTile && previewImage && (
          <img
            src={cfSrcForImage(
              previewImage,
              isMobile ? CLEAN_TILE_SIZE_MOBILE : CLEAN_TILE_SIZE_DESKTOP,
            )}
            alt=""
            className={styles.cleanTileImage}
          />
        )}
      </Link>
    );
  },
);

Card.displayName = 'Card';

export default observer(Card);
